import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "@/App.css";
import StepUpModal from "./components/StepUpModal";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { 
  Car, 
  Gauge, 
  Settings, 
  Upload, 
  ChevronRight, 
  ChevronLeft,
  Check,
  Zap,
  Clock,
  Rocket,
  AlertCircle,
  X,
  ZoomIn,
  FileText,
  LayoutDashboard,
  ShoppingCart,
  Database,
  ClipboardList,
  FolderOpen,
  History,
  MessageSquare,
  Receipt,
  FileStack,
  Newspaper,
  BookOpen,
  BarChart3,
  Wallet,
  ChevronDown,
  Bell,
  Globe,
  User,
  LogOut,
  CreditCard,
  Menu,
  Phone,
  Search,
  Download,
  Eye,
  Plus,
  Filter,
  Calendar,
  TrendingUp,
  Users,
  Package,
  Mail,
  MapPin,
  Send,
  Paperclip,
  MoreVertical,
  Edit,
  Trash2,
  XCircle,
  CheckCircle,
  RefreshCw,
  Building2
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import LegalDocModal from "./components/LegalDocModal";

// Real vehicle data
const vehicleData = {
  types: ["Truck", "Bus", "Production machines/Construction"],
  manufacturers: {
    "Truck": ["Mack", "Renault", "UD Truck", "Volvo"],
    "Bus": ["Volvo"],
    "Production machines/Construction": ["Gasparin", "Volvo"]
  },
  models: {
    "Gasparin": ["GI 118 C Olimpo"],
    "Mack": ["MP8 Euro-6 2019+"],
    "Renault": ["Renault Midlum 2018+ (EURO-6)", "Premium 2018+ (EURO-6)"],
    "UD Truck": ["Quon"],
    "Volvo": [
      "VN 500", "FMX 460", "FM (2005) 500", "FM (2005) 460", "FM (2005) 420", 
      "FM (2005) 380", "FL (2006) 280", "FL (2006) 210", "FH (2012) 750", 
      "FH (2012) 650", "FH (2012) 550", "FH (2012) 540", "FH (2012) 500", 
      "FH (2012) 460", "FH (2012) 440", "FH (2012) 430", "FH (2012) 420", 
      "FH (2012) 380", "FE 350", "Excavator EC530E/EC550E", "B8R", "B11R"
    ]
  },
  engines: {
    "GI 118 C Olimpo": ["All engines"],
    "MP8 Euro-6 2019+": ["12.8TDI E6 420-520hp"],
    "Renault Midlum 2018+ (EURO-6)": ["7.8TDI DTi8 210-280hp"],
    "Premium 2018+ (EURO-6)": ["10.8TDI DTi11 380-460HP", "12.8TDI DTi13 440-520HP"],
    "Quon": ["7.7 L", "10.8 L"],
    "VN 500": ["12.8L 397 kW/532 Hp"],
    "FMX 460": ["12.8L 338 kW/453 Hp"],
    "FM (2005) 500": ["12.8L 368 kW/494 Hp"],
    "FM (2005) 460": ["12.8L 345 kW/463 Hp"],
    "FM (2005) 420": ["12.8L 313 kW/420 Hp"],
    "FM (2005) 380": ["12.8L 279 kW/374 Hp"],
    "FL (2006) 280": ["7.7L 210 kW/282 Hp"],
    "FL (2006) 210": ["5.1L 154 kW/206 Hp"],
    "FH (2012) 750": ["16.1L 552 kW/740 Hp"],
    "FH (2012) 650": ["16.1L 478 kW/641 Hp"],
    "FH (2012) 550": ["16.1L 405 kW/543 Hp"],
    "FH (2012) 540": ["12.8L 397 kW/532 Hp"],
    "FH (2012) 500": ["12.8L 375 kW/502 Hp"],
    "FH (2012) 460": ["12.8L 338 kW/454 Hp", "12.8L 343 kW/460 Hp"],
    "FH (2012) 440": ["12.8L 323 kW/433 Hp"],
    "FH (2012) 430": ["10.8L 317 kW/430 Hp"],
    "FH (2012) 420": ["12.8L 309 kW/414 Hp"],
    "FH (2012) 380": ["12.8L 283 kW/380 Hp"],
    "FE 350": ["7.7L 258 kW/350 Hp"],
    "Excavator EC530E/EC550E": ["12.8TDI"],
    "B8R": ["7.7L 258 kW/350 Hp"],
    "B11R": ["10.8L 331 kW/444 Hp", "10.8L 345 kW/463 Hp", "10.8L 323 kW/433 Hp"]
  },
  ecuTypes: {
    "All engines": ["TRW EMS2.4 and ACM"],
    "12.8TDI E6 420-520hp": ["TRW EMS2.4 and ACM"],
    "7.8TDI DTi8 210-280hp": ["TRW EMS2.4 and ACM"],
    "10.8TDI DTi11 380-460HP": ["TRW EMS2.4 and ACM"],
    "12.8TDI DTi13 440-520HP": ["TRW EMS2.4 and ACM"],
    "7.7 L": ["TRW EMS2.4 and ACM"],
    "10.8 L": ["TRW EMS2.4 and ACM"],
    "12.8L 397 kW/532 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 338 kW/453 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 368 kW/494 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 345 kW/463 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 313 kW/420 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 279 kW/374 Hp": ["TRW EMS2.4 and ACM"],
    "7.7L 210 kW/282 Hp": ["TRW EMS2.4 and ACM"],
    "5.1L 154 kW/206 Hp": ["TRW EMS2.4 and ACM"],
    "16.1L 552 kW/740 Hp": ["TRW EMS2.4 and ACM"],
    "16.1L 478 kW/641 Hp": ["TRW EMS2.4 and ACM"],
    "16.1L 405 kW/543 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 375 kW/502 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 338 kW/454 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 343 kW/460 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 323 kW/433 Hp": ["TRW EMS2.4 and ACM"],
    "10.8L 317 kW/430 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 309 kW/414 Hp": ["TRW EMS2.4 and ACM"],
    "12.8L 283 kW/380 Hp": ["TRW EMS2.4 and ACM"],
    "7.7L 258 kW/350 Hp": ["TRW EMS2.4 and ACM"],
    "12.8TDI": ["TRW EMS2.4 and ACM"],
    "10.8L 331 kW/444 Hp": ["TRW EMS2.4 and ACM"],
    "10.8L 345 kW/463 Hp": ["TRW EMS2.4 and ACM"],
    "10.8L 323 kW/433 Hp": ["TRW EMS2.4 and ACM"]
  }
};

const gearboxTypes = ["Automatic transmission", "Manual transmission", "CVT", "DSG/DCT", "I-Shift", "Opticruise"];
const modelYears = Array.from({ length: 30 }, (_, i) => (2025 - i).toString());

const tuningOptions = [
  { id: "dpf", icon: "DPF", name: "DPF/FAP - Delete", price: 80, instruction: "Please if you want to delete DPF, as EURO-6 vehicles have AdBlue and you need to order DPF+AdBlue OFF.\n\nPlease attach reading for both ECU's - engine ECU and adblue ACM ECU.", image: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=600&h=400&fit=crop" },
  { id: "egr", icon: "EGR", name: "EGR/AGR Removal", price: 70, instruction: "EGR removal improves engine response and reduces carbon buildup. After software delete, physically block the EGR valve.", image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&h=400&fit=crop" },
  { id: "adblue", icon: "AdBlue", name: "SCR/AdBlue/DEF/UREA Delete", price: 140, instruction: "Please attach reading for both ECU's - engine ECU and adblue ACM ECU. We offer AdBlue consumption decrease for same price.", image: "https://customer-assets.emergentagent.com/job_order-wizard-20/artifacts/nm8r4ux4_image.png" },
  { id: "stage1", icon: "S1", name: "Stage 1 tuning file", price: 105, instruction: "Stage 1 optimizes engine maps for increased power. Typical gains: Diesel +25-35%.", image: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&h=400&fit=crop" },
  { id: "speedlimiter", icon: "V+", name: "Speed limiter/ Vmax modify", price: 80, instruction: "Remove or modify the electronic speed limiter. Specify desired limit in description.", image: null },
  { id: "throttle", icon: "X", name: "Throttle Valve TVA Disable", price: 70, instruction: "Disable throttle valve actuator error codes and functionality.", image: null },
  { id: "swirl", icon: "SWIRL", name: "Swirl valve/Flaps OFF", price: 75, instruction: "Disable swirl flaps. Recommended after physical removal.", image: null },
  { id: "dtc", icon: "DTC", name: "DTC's selectivity OFF", price: 50, instruction: "Turn off Check Engine light. Specify P-codes in description.", image: null },
  { id: "stage2", icon: "S2", name: "Stage 1+/Stage 2 tuning file", price: 160, instruction: "Stage 2 requires hardware mods. Specify your modifications.", image: null }
];

const fileTypes = ["File is original", "File is modified", "File with fault"];
const readingMethods = ["CMD Tricore Bootload", "KTAG", "Kess V2", "PCMFlash", "BitBox", "Other"];
const priorities = [
  { id: "highest", name: "Highest", time: "10-30 min", extra: "+20%", color: "text-red-600", bg: "bg-red-50", border: "border-red-300" },
  { id: "high", name: "High", time: "30-90 min", extra: "", color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-300" },
  { id: "medium", name: "Medium", time: "1-3 hours", extra: "", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-300" },
  { id: "low", name: "Low", time: "3-6 hours", extra: "", color: "text-green-600", bg: "bg-green-50", border: "border-green-300" }
];

// Menu items
const menuItems = [
  { id: "dashboard", icon: LayoutDashboard, label: "Dashboard", badge: null },
  { id: "neworder", icon: ShoppingCart, label: "New order / prices", badge: null, active: true },
  { id: "orifiles", icon: Database, label: "Ori Files Database", badge: null },
  { id: "orders", icon: ClipboardList, label: "Orders Info", badge: null },
  { id: "etc", icon: FolderOpen, label: "Etc", badge: null, hasSubmenu: true },
  { id: "history", icon: History, label: "Files History", badge: null },
  { id: "tickets", icon: MessageSquare, label: "Tickets", badge: null },
  { id: "transactions", icon: Receipt, label: "Transactions", badge: null },
  { id: "invoices", icon: FileStack, label: "Invoices", badge: null },
  { id: "news", icon: Newspaper, label: "News", badge: 3 },
  { id: "catalog", icon: BookOpen, label: "Tuning Catalog", badge: null },
  { id: "settings", icon: Settings, label: "Settings", badge: null },
  { id: "dynograph", icon: BarChart3, label: "Dynograph", badge: null },
  { id: "recharge", icon: CreditCard, label: "Recharge Account", badge: null },
  { id: "analytics", icon: BarChart3, label: "Analytics", badge: null },
];

const OptionIcon = ({ icon, selected }) => (
  <div className={`w-11 h-11 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
    selected ? 'bg-cyan-500 border-cyan-500 text-white' : 'bg-cyan-50 border-cyan-400 text-cyan-600'
  }`}>
    {icon}
  </div>
);

// Sidebar Component
const Sidebar = ({ sidebarOpen, activePage, setActivePage }) => (
  <aside className={`fixed left-0 top-0 h-full bg-slate-800 text-white transition-all duration-300 z-40 
    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    ${sidebarOpen ? 'w-64' : 'w-64 lg:w-20'}
    flex flex-col`}
  >
    {/* Logo */}
    <div className="h-16 flex items-center justify-center border-b border-slate-700 px-4">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
          <Zap className="w-6 h-6 text-white" />
        </div>
        <span className={`font-bold text-lg ${sidebarOpen ? '' : 'lg:hidden'}`}>AUTO-REMAP</span>
      </div>
    </div>

    {/* Menu */}
    <nav className="flex-1 overflow-y-auto py-4">
      <div className={`px-4 mb-2 ${sidebarOpen ? '' : 'lg:text-center'}`}>
        <span className={`text-xs text-slate-500 uppercase tracking-wider ${sidebarOpen ? '' : 'lg:hidden'}`}>
          Panel
        </span>
        <span className={`text-xs text-slate-500 uppercase tracking-wider ${sidebarOpen ? 'hidden' : 'hidden lg:inline'}`}>
          •••
        </span>
      </div>
      
      {menuItems.map((item) => (
        <button
          key={item.id}
          onClick={() => setActivePage(item.id)}
          className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-colors ${
            activePage === item.id 
              ? 'bg-cyan-500 text-white' 
              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
          }`}
        >
          <item.icon className="w-5 h-5 flex-shrink-0" />
          <span className={`flex-1 text-sm ${sidebarOpen ? '' : 'lg:hidden'}`}>{item.label}</span>
          {item.badge && (
            <span className={`bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ${sidebarOpen ? '' : 'lg:hidden'}`}>{item.badge}</span>
          )}
          {item.hasSubmenu && <ChevronDown className={`w-4 h-4 ${sidebarOpen ? '' : 'lg:hidden'}`} />}
        </button>
      ))}
    </nav>

    {/* Messengers */}
    <div className="px-4 py-3 border-t border-slate-700">
      {sidebarOpen && <span className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Contact Us</span>}
      <div className={`flex ${sidebarOpen ? 'gap-2' : 'flex-col gap-2 items-center'}`}>
        <button className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
          <Phone className="w-4 h-4" />
        </button>
        <button className="w-9 h-9 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors">
          <MessageSquare className="w-4 h-4" />
        </button>
        <button className="w-9 h-9 bg-sky-500 rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
        </button>
        <button className="w-9 h-9 bg-purple-500 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2zm.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 012.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.188 8.188 0 01-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24zM8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01z"/></svg>
        </button>
      </div>
    </div>

    {/* Account Status */}
    <div className="px-4 py-4 border-t border-slate-700 bg-slate-900">
      {sidebarOpen ? (
        <>
          <span className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Account Status</span>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-400">Balance:</span>
            <span className="text-lg font-bold text-cyan-400">€142</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-400">EVC Balance:</span>
            <span className="text-lg font-bold text-green-400">€71.43</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Reserved:</span>
            <span className="text-sm text-amber-400">€0</span>
          </div>
        </>
      ) : (
        <div className="text-center space-y-1">
          <div>
            <Wallet className="w-5 h-5 mx-auto text-cyan-400" />
            <span className="text-xs text-cyan-400 font-bold block">€142</span>
          </div>
          <div className="pt-1 border-t border-slate-700">
            <span className="text-[10px] text-slate-500 block">EVC</span>
            <span className="text-xs text-green-400 font-bold block">€71.43</span>
          </div>
        </div>
      )}
    </div>
  </aside>
);

// Header Component
const Header = ({ sidebarOpen, setSidebarOpen, serverTime, setActivePage }) => (
  <header className={`fixed top-0 right-0 h-16 bg-white border-b border-slate-200 z-30 transition-all duration-300 
    ${sidebarOpen ? 'lg:left-64' : 'lg:left-20'} left-0`}
  >
    <div className="h-full px-3 sm:px-4 flex items-center justify-between">
      {/* Left side */}
      <div className="flex items-center gap-2 sm:gap-4">
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <Menu className="w-5 h-5 text-slate-600" />
        </button>
        
        {/* Server Time */}
        <div className="hidden md:flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-lg">
          <Clock className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-600">{serverTime}</span>
        </div>
        
        {/* Order Queue */}
        <div className="hidden lg:flex items-center gap-2 bg-cyan-50 text-cyan-700 px-3 py-1.5 rounded-lg border border-cyan-200">
          <ClipboardList className="w-4 h-4" />
          <span className="text-sm font-medium">Order queue: 3</span>
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Recharge Button - Hide on mobile */}
        <button 
          onClick={() => setActivePage('recharge')}
          className="hidden lg:flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all text-sm font-medium"
        >
          <CreditCard className="w-4 h-4" />
          Recharge Account
        </button>

        {/* Balance - Simplified on mobile */}
        <div className="flex items-center gap-1.5 sm:gap-2 bg-slate-100 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg">
          <div className="text-right">
            <div className="text-[10px] sm:text-xs text-slate-500">Balance</div>
            <div className="text-xs sm:text-sm font-bold text-cyan-600">€142</div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-slate-300"></div>
          <div className="text-right hidden sm:block">
            <div className="text-xs text-slate-500">EVC Balance</div>
            <div className="text-sm font-bold text-green-600">€71.43</div>
          </div>
          <div className="hidden md:block w-px h-8 bg-slate-300"></div>
          <div className="text-right hidden md:block">
            <div className="text-xs text-slate-500">Reserved</div>
            <div className="text-sm font-medium text-amber-600">€0.00</div>
          </div>
        </div>

        {/* Language switcher — customer-only top nav. Replaces the
            previous Globe icon placeholder. Hidden on the admin panel
            because admin UI is EN-only by product decision. */}
        <LanguageSwitcher compact />

        {/* Notifications */}
        <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors relative">
          <Bell className="w-5 h-5 text-slate-600" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        </button>

        {/* User Menu */}
        <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
          <div className="w-9 h-9 bg-cyan-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-cyan-600" />
          </div>
          <div className="hidden md:block">
            <div className="text-sm font-medium text-slate-800">Dmitry</div>
            <div className="text-xs text-slate-500">Pro Account</div>
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </div>
      </div>
    </div>
  </header>
);


// ============================================
// PAGE COMPONENTS
// ============================================

// Dashboard Page Component
// Dashboard Page Component
const DashboardPage = ({ setActivePage }) => {
  const [statsFilter, setStatsFilter] = useState('week');
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };
  
  const getCurrentDay = () => {
    return currentTime.getDay(); // 0 = Sunday, 1 = Monday, etc.
  };
  
  const workingHours = [
    { day: 'MONDAY', hours: '08:00 - 22:00' },
    { day: 'TUESDAY', hours: '08:00 - 22:00' },
    { day: 'WEDNESDAY', hours: '08:00 - 22:00' },
    { day: 'THURSDAY', hours: '08:00 - 22:00' },
    { day: 'FRIDAY', hours: '08:00 - 22:00' },
    { day: 'SATURDAY', hours: '08:00 - 20:00' },
    { day: 'SUNDAY', hours: '10:00 - 18:00' }
  ];
  
  return (
    <div className="space-y-6">
      {/* Top Section: Time, Date, Buttons */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clock */}
        <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium opacity-90">Current Time</span>
          </div>
          <div className="text-3xl font-bold mb-1">{formatTime(currentTime)}</div>
          <div className="text-sm opacity-90">GMT+3</div>
        </div>
        
        {/* Date */}
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <span className="text-xs font-medium text-slate-600">Today's Date</span>
          </div>
          <div className="text-2xl font-bold text-slate-800 mb-1">{formatDate(currentTime)}</div>
          <div className="text-xs text-slate-500">{currentTime.toLocaleDateString('en-US', { weekday: 'long' })}</div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="text-xs font-medium text-slate-600 mb-3">Quick Actions</div>
          <div className="space-y-2">
            <button 
              onClick={() => setActivePage('neworder')}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2.5 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              New Order
            </button>
            <button className="w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-200 transition-all font-medium text-sm">
              <CreditCard className="w-4 h-4" />
              Recharge Account
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-cyan-600" />
            </div>
            <span className="text-xs text-slate-500">Total</span>
          </div>
          <div className="text-3xl font-bold text-slate-800 mb-1">247</div>
          <div className="text-sm text-slate-500">Total Orders</div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-xs text-slate-500">This month</span>
          </div>
          <div className="text-3xl font-bold text-slate-800 mb-1">186</div>
          <div className="text-sm text-slate-500">Completed</div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <span className="text-xs text-slate-500">Active</span>
          </div>
          <div className="text-3xl font-bold text-slate-800 mb-1">8</div>
          <div className="text-sm text-slate-500">In Progress</div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <span className="text-xs text-slate-500">This month</span>
          </div>
          <div className="text-3xl font-bold text-slate-800 mb-1">3</div>
          <div className="text-sm text-slate-500">Cancelled</div>
        </div>
      </div>

      {/* Opening Times Table */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-cyan-600" />
          <h3 className="text-lg font-bold text-slate-800">Opening Times GMT+3</h3>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {workingHours.map((item, idx) => {
            const dayIndex = idx === 6 ? 0 : idx + 1; // Adjust for Sunday being 0
            const isToday = getCurrentDay() === dayIndex;
            
            return (
              <div 
                key={item.day} 
                className={`p-4 rounded-lg border-2 text-center transition-all ${
                  isToday 
                    ? 'border-cyan-500 bg-cyan-50' 
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className={`text-xs font-bold mb-2 ${
                  isToday ? 'text-cyan-700' : idx === 6 ? 'text-red-600' : 'text-slate-700'
                }`}>
                  {item.day}
                </div>
                <div className={`text-sm ${isToday ? 'text-cyan-600 font-semibold' : 'text-slate-600'}`}>
                  {item.hours}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chart Section */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-slate-800">Orders Analytics</h2>
          <div className="flex gap-2">
            {['week', 'month', 'year'].map(filter => (
              <button
                key={filter}
                onClick={() => setStatsFilter(filter)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statsFilter === filter
                    ? 'bg-cyan-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {filter === 'week' ? 'Week' : filter === 'month' ? 'Month' : 'Year'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Simple Bar Chart Visualization */}
        <div className="space-y-4">
          <div className="flex items-end gap-2 h-48">
            {[42, 65, 38, 55, 71, 48, 62].map((value, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full bg-gradient-to-t from-cyan-500 to-cyan-300 rounded-t-lg hover:from-cyan-600 hover:to-cyan-400 transition-all cursor-pointer" 
                     style={{ height: `${value}%` }}
                     title={`${value} orders`}
                ></div>
                <span className="text-xs text-slate-500">
                  {statsFilter === 'week' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx] : 
                   statsFilter === 'month' ? `Week ${idx + 1}` : 
                   ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'][idx]}
                </span>
              </div>
            ))}
          </div>
          
          <div className="flex items-center justify-center gap-6 pt-4 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-slate-600">Completed: 186</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-sm text-slate-600">Cancelled: 3</span>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Contacts */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-cyan-600" />
            <h3 className="text-lg font-bold text-slate-800">Company Information</h3>
          </div>
          
          <div className="space-y-4">
            {/* Company Name */}
            <div className="pb-3 border-b border-slate-200">
              <div className="text-xs text-slate-500 mb-1">Company Name</div>
              <div className="text-sm font-bold text-slate-800">ORMAR SOLUTIONS INC</div>
            </div>
            
            {/* Address */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">Address</div>
                <div className="text-sm text-slate-700">96 LINWOOD PLAZA, SUITE 440</div>
                <div className="text-sm text-slate-700">FORT LEE, NJ 07024 USA</div>
              </div>
            </div>
            
            {/* Emails */}
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-cyan-600" />
                <div className="flex-1">
                  <span className="text-xs text-slate-500">Main Email:</span>
                  <span className="text-sm text-cyan-600 ml-2 font-medium">info@auto-remap.com</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-blue-600" />
                <div className="flex-1">
                  <span className="text-xs text-slate-500">Support Email:</span>
                  <span className="text-sm text-blue-600 ml-2 font-medium">info@auto-remap.com</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-green-600" />
                <div className="flex-1">
                  <span className="text-xs text-slate-500">Billing Email:</span>
                  <span className="text-sm text-green-600 ml-2 font-medium">info@auto-remap.com</span>
                </div>
              </div>
            </div>
            
            {/* Messengers */}
            <div className="pt-4 border-t border-slate-200">
              <div className="text-xs font-medium text-slate-600 mb-3">Contact via Messengers</div>
              <div className="flex items-center gap-3">
                <button className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors shadow-md">
                  <Phone className="w-5 h-5 text-white" />
                </button>
                <button className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors shadow-md">
                  <MessageSquare className="w-5 h-5 text-white" />
                </button>
                <button className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors shadow-md">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
                </button>
                <button className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors shadow-md">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2zm.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 012.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.188 8.188 0 01-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24zM8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01z"/></svg>
                </button>
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="pt-4 border-t border-slate-200">
              <div className="text-xs font-medium text-slate-600 mb-3">We Accept</div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <svg className="h-6" viewBox="0 0 48 32" fill="none">
                    <rect width="48" height="32" rx="4" fill="#1434CB"/>
                    <path d="M18 11h12v10H18z" fill="#FF5F00"/>
                    <path d="M19 16a6 6 0 0 1 6-6 6 6 0 1 0 0 12 6 6 0 0 1-6-6z" fill="#EB001B"/>
                    <path d="M31 16a6 6 0 0 1-6 6 6 6 0 0 0 0-12 6 6 0 0 1 6 6z" fill="#F79E1B"/>
                  </svg>
                  <span className="text-xs font-medium text-slate-700">Mastercard</span>
                </div>
                
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <svg className="h-6" viewBox="0 0 48 32" fill="none">
                    <rect width="48" height="32" rx="4" fill="#0066B2"/>
                    <path d="M20 11l-4 10h3l4-10h-3zm8 0l-4 10h3l4-10h-3z" fill="white"/>
                  </svg>
                  <span className="text-xs font-medium text-slate-700">Visa</span>
                </div>
                
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <svg className="h-6" viewBox="0 0 48 32" fill="none">
                    <rect width="48" height="32" rx="4" fill="#003087"/>
                    <path d="M16 12h-4l-2 8h2l.5-2h2l.5 2h2l-2-8zm-3 5l1-3 1 3h-2zm10-5h-2l-2 8h2l2-8zm7 0h-3l-2 8h2l.5-2h2.5c1.5 0 2.5-1 2.5-3s-1-3-2.5-3zm-1 5h-1.5l.5-2h1.5c.5 0 1 .5 1 1s-.5 1-1.5 1z" fill="white"/>
                    <path d="M34 12h-4l-2 8h2l.5-2h2l.5 2h2l-2-8zm-3 5l1-3 1 3h-2z" fill="#009CDE"/>
                  </svg>
                  <span className="text-xs font-medium text-slate-700">PayPal</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 mb-4">Recent Orders</h3>
          
          <div className="space-y-3">
            {[
              { id: '230525-5', brand: 'MASERATI MARELLI', status: 'completed', date: '12.03.2024 17:44' },
              { id: '230525-4', brand: 'Hyundai', status: 'working', date: '07.04.2024 19:14' },
              { id: '230525-3', brand: 'Volvo FH', status: 'completed', date: '30.03.2024 14:27' },
              { id: '230525-2', brand: 'Mercedes', status: 'completed', date: '28.03.2024 16:22' }
            ].map(order => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-800">#{order.id}</div>
                    <div className="text-xs text-slate-500">{order.brand}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs font-medium px-2 py-1 rounded ${
                    order.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {order.status === 'completed' ? 'Completed' : 'Working'}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{order.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ORI Files Database Page Component
const ORIFilesPage = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  const handleSearch = () => {
    // Mock search results
    setSearchResults([
      { brand: 'ACM OPEL/VAUXHALL', engine: '1.2D8V (4D)', hw: '110273765', sw: '110257392', ecu: 'BOSCH EDC16 C39', price: '€20' },
      { brand: 'FIAT D 2000', engine: '2.8D8V (4D)', hw: '110273912', sw: '55247829', ecu: 'BOSCH EDC16 C39', price: '€20' },
      { brand: 'FIAT 15.000', engine: '5.8D8V (4D)', hw: '110273904', sw: '55257394', ecu: 'BOSCH EDC16 C39', price: '€20' },
      { brand: 'FIAT DTH13', engine: '13D8V (4D)', hw: '110274700', sw: '55257390', ecu: 'BOSCH EDC16 C39', price: '€20' },
      { brand: 'FIAT DTH13', engine: '13D8V (4D)', hw: '110274745', sw: '55257390', ecu: 'BOSCH EDC16 C39', price: '€20' },
      { brand: 'GREAT WALL HOVER', engine: '2.4D8V (4D)', hw: '230273765', sw: '58247839', ecu: 'BOSCH EDC17 C11', price: '€20' }
    ]);
    setShowResults(true);
  };
  
  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <p className="mb-2">
          On our web site you can find original <strong>ECU files</strong> for more than <strong>210 000</strong> different SW numbers and <strong>HW</strong> hardware versions. 
          If you are looking for an original ECU file, you are on correct page now. You can search by <strong>brand name, model, engine, software, hardware, calibration number</strong> and so on and request <strong>original ECU file</strong>.
        </p>
        <p className="text-red-600 font-medium">
          The will help you to understand what modifications was done in your modified ECU file after booting only by writing in ECU with Alientech devices.
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Select>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select brand name" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fiat">FIAT</SelectItem>
              <SelectItem value="volvo">VOLVO</SelectItem>
              <SelectItem value="mack">MACK</SelectItem>
            </SelectContent>
          </Select>
          
          <Select>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ducato">Ducato</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
            </SelectContent>
          </Select>
          
          <Select>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select type of engine" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="diesel">Diesel</SelectItem>
              <SelectItem value="petrol">Petrol</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Select>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select ecu brand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bosch">BOSCH</SelectItem>
              <SelectItem value="siemens">SIEMENS</SelectItem>
            </SelectContent>
          </Select>
          
          <Select>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select ecu model" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="edc16">EDC16</SelectItem>
              <SelectItem value="edc17">EDC17</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Search by HW (SW, DTC or calibration number)</Label>
            <Input placeholder="For example: 110273765" className="bg-white" />
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Search by SW</Label>
            <Input placeholder="For example: 100251007181" className="bg-white" />
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Search by HW</Label>
            <Input placeholder="For example: 1037371170" className="bg-white" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Search by SW / registrar</Label>
            <Input placeholder="For example: 100264003701" className="bg-white" />
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Search by Calibration</Label>
            <Input placeholder="For example: 1000340304" className="bg-white" />
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Search by Diamos</Label>
            <Input placeholder="For example: 1037394" className="bg-white" />
          </div>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={handleSearch}
            className="px-6 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors font-medium"
          >
            SEARCH
          </button>
          <button className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors">
            Clear
          </button>
        </div>
      </div>

      {/* Search Results */}
      {showResults && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Brand name and model</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Engine type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Hardware number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Software number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Software upgrade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Calibration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Ecu model</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Price €</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {searchResults.map((result, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-slate-800">{result.brand}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{result.engine}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{result.hw}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{result.sw}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">-</td>
                    <td className="px-4 py-3 text-sm text-slate-600">-</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{result.ecu}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">{result.price}</td>
                    <td className="px-4 py-3">
                      <button className="mx-auto block px-4 py-1.5 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors">
                        Buy
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-center">
            <button className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors text-sm font-medium">
              Load more
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Orders Info Page Component
// Order Detail Page Component
const OrderDetailPage = ({ order, onBack }) => {
  const [newMessage, setNewMessage] = useState('');
  
  const ticketMessages = [
    {
      id: 1,
      sender: 'Michael',
      type: 'customer',
      date: '30.10.2024 14:27',
      message: 'Hi, can I get status of my order file? we are waiting... Thanks!'
    },
    {
      id: 2,
      sender: 'Admin',
      type: 'admin',
      date: '30.10.2024 15:12',
      message: "Hi! We've started working on your order. The test driver feedback regarding the GPS map hasn't been finalized. Hold on!"
    },
    {
      id: 3,
      sender: 'Michael',
      type: 'customer',
      date: '30.10.2024 16:45',
      message: 'DTC error? How long should I wait? Can you check it please?'
    }
  ];
  
  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Orders</span>
      </button>

      {/* Order Header */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Detail for order #{order.id}</h1>
            <div className="flex items-center gap-4 text-sm opacity-90">
              <span>● Created: 30.09.2024 02:06 GMT+3</span>
              <span>○ Paid: 01.05.2024 02:08 GMT+3</span>
            </div>
            <div className="text-lg font-bold mt-2">Total: {order.amount}</div>
          </div>
          <button className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-medium backdrop-blur-sm">
            Exit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Order Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vehicle Information */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
              <div className="flex items-center gap-2 text-white">
                <Car className="w-5 h-5" />
                <h3 className="text-lg font-bold">Vehicle & Order Information</h3>
              </div>
            </div>
            
            <div className="p-6">
              {/* Vehicle Type & Manufacturer */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Car className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-slate-500 mb-1">Vehicle Type</div>
                    <div className="text-base font-bold text-slate-800">Car</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Gauge className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-slate-500 mb-1">Manufacturer</div>
                    <div className="text-base font-bold text-slate-800">{order.manufacturer}</div>
                  </div>
                </div>
              </div>
              
              {/* Model & Engine */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pb-6 border-b border-slate-200">
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">Model</div>
                  <div className="px-4 py-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-sm font-bold text-slate-800">{order.model}</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">Engine</div>
                  <div className="px-4 py-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-sm font-bold text-slate-800">{order.engine}</div>
                  </div>
                </div>
              </div>
              
              {/* ECU & Gearbox */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pb-6 border-b border-slate-200">
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">ECU Type</div>
                  <div className="px-4 py-2.5 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="text-sm font-bold text-purple-800">{order.ecuType}</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">Gearbox Type</div>
                  <div className="px-4 py-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-sm font-semibold text-slate-800">Automatic transmission</div>
                  </div>
                </div>
              </div>
              
              {/* Model Year & Engine Power */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 pb-6 border-b border-slate-200">
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">Model Year</div>
                  <div className="px-4 py-2.5 bg-slate-50 rounded-lg border border-slate-200 text-center">
                    <div className="text-lg font-bold text-slate-800">2023</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">Engine Power (HP)</div>
                  <div className="px-4 py-2.5 bg-amber-50 rounded-lg border border-amber-200 text-center">
                    <div className="text-lg font-bold text-amber-700">350 HP</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">Engine Power (kW)</div>
                  <div className="px-4 py-2.5 bg-amber-50 rounded-lg border border-amber-200 text-center">
                    <div className="text-lg font-bold text-amber-700">257 kW</div>
                  </div>
                </div>
              </div>
              
              {/* Reading Method & File Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pb-6 border-b border-slate-200">
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">Reading Method</div>
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 rounded-lg border border-green-200">
                    <Upload className="w-4 h-4 text-green-600" />
                    <div className="text-sm font-bold text-green-700">OBD</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">File Type</div>
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 rounded-lg border border-blue-200">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <div className="text-sm font-bold text-blue-700">Original</div>
                  </div>
                </div>
              </div>
              
              {/* VIN */}
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">VIN (Vehicle Identification Number)</div>
                <div className="px-4 py-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="text-sm font-mono text-slate-700">{order.vin || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-cyan-600" />
              <h3 className="text-lg font-bold text-slate-800">Service Details</h3>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="text-xs text-slate-600 mb-1">→ Ordered options:</div>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full font-medium">
                    {order.service}
                  </span>
                </div>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-600 mb-1">→ Description:</div>
                <div className="text-sm text-slate-700">{order.description || 'No additional description'}</div>
              </div>
            </div>
          </div>

          {/* Ticket Messages */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-6 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-600" />
                <h3 className="text-lg font-bold text-slate-800">Messages in Ticket</h3>
              </div>
            </div>
            
            <div className="p-6 bg-white max-h-96 overflow-y-auto">
              <div className="space-y-4">
                {ticketMessages.map((msg) => (
                  <div key={msg.id} className={`flex gap-3 ${msg.type === 'customer' ? 'justify-start' : 'justify-end'}`}>
                    {msg.type === 'customer' && (
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-orange-600" />
                      </div>
                    )}
                    
                    <div className={`max-w-md ${msg.type === 'customer' ? '' : 'order-1'}`}>
                      <div className={`px-4 py-3 rounded-lg ${
                        msg.type === 'customer' 
                          ? 'bg-slate-100 border border-slate-200' 
                          : 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white'
                      }`}>
                        <div className={`text-xs ${msg.type === 'customer' ? 'text-slate-500' : 'text-white/80'} mb-1 font-medium`}>
                          {msg.sender} • {msg.date}
                        </div>
                        <div className="text-sm">{msg.message}</div>
                      </div>
                    </div>
                    
                    {msg.type === 'admin' && (
                      <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-cyan-600" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 bg-slate-50">
              <div className="flex gap-3">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
                  rows={3}
                />
                <div className="flex flex-col gap-2">
                  <button className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm font-medium flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    File
                  </button>
                  <button className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors text-sm font-medium flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Status & Actions */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="text-sm font-bold text-slate-700 mb-4">Order Status</h3>
            
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500 mb-1">Status</div>
                <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full ${
                  order.status === 'Completed' ? 'bg-green-100 text-green-700' : 
                  order.status === 'Working' ? 'bg-cyan-100 text-cyan-700' : 
                  'bg-red-100 text-red-700'
                }`}>
                  {order.status}
                </span>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500 mb-1">Priority</div>
                <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full ${
                  order.priority === 'MS' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {order.priority}
                </span>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500 mb-1">ID Ticket</div>
                <div className="text-sm font-medium text-cyan-600">Open</div>
              </div>
              
              {order.status === 'Working' && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="text-xs text-amber-600 mb-1 font-medium">⏱ Waiting time</div>
                  <div className="text-2xl font-bold text-amber-700">2:34 hours</div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="text-sm font-bold text-slate-700 mb-4">Actions</h3>
            
            <div className="space-y-3">
              <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all font-medium shadow-md">
                <Download className="w-4 h-4" />
                Download Original
              </button>
              
              {order.status === 'Completed' && (
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:from-blue-600 hover:to-cyan-700 transition-all font-medium shadow-md">
                  <Download className="w-4 h-4" />
                  Download Result
                </button>
              )}
              
              {order.status !== 'Completed' && (
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium">
                  <XCircle className="w-4 h-4" />
                  Cancel Order
                </button>
              )}
              
              <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium">
                <RefreshCw className="w-4 h-4" />
                Refresh Status
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
            <div className="text-xs text-cyan-700 font-medium mb-2">💡 Need Help?</div>
            <p className="text-xs text-slate-600 mb-3">
              Use the message section to communicate with our support team about this order.
            </p>
            <button className="w-full px-4 py-2 bg-white text-cyan-600 rounded-lg hover:bg-cyan-50 transition-colors text-sm font-medium">
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Orders Info Page Component
const OrdersInfoPage = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  const orders = [
    {
      id: '230525-5',
      date: '12.03.2024 17:44',
      service: 'EGR/SCR Removal',
      manufacturer: 'MASERATI MARELLI',
      model: 'M96XT GRANT-28040 GC301',
      engine: '4276.050F',
      ecuType: 'Bosch M0CT830M',
      vin: 'software upgrade, software update, calculation model',
      description: 'For multiple platforms: press CTRL + A! Model:',
      priority: 'Low',
      amount: '€20',
      status: 'Completed'
    },
    {
      id: '230525-4',
      date: '18.05.2025 14:07',
      service: 'Stage 1 Tuning', 
      manufacturer: 'Hyundai',
      model: 'County',
      engine: '3.9TDI D4DD',
      ecuType: 'Delphi DCM3.5',
      vin: '-',
      description: 'software registered, ecu name register, mode: MASTERKEY/HSUMD, engine code: DTC31, file: QMU2 V-16',
      priority: 'MS',
      amount: '€180',
      status: 'Working'
    },
    {
      id: '230525-3',
      date: '07.04.2024 10:22',
      service: 'DPF+AdBlue OFF', 
      manufacturer: 'Mercedes',
      model: 'Actros',
      engine: '15.6L OM471',
      ecuType: 'Bosch MD1CP001',
      vin: 'WDB9634161L123456',
      description: '',
      priority: 'High',
      amount: '€145',
      status: 'Completed'
    }
  ];
  
  if (selectedOrder) {
    return <OrderDetailPage order={selectedOrder} onBack={() => setSelectedOrder(null)} />;
  }
  
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Brand</Label>
            <Input placeholder="Brand" className="bg-white" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Model</Label>
            <Input placeholder="Model" className="bg-white" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Engine</Label>
            <Input placeholder="Engine" className="bg-white" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Ecu type</Label>
            <Input placeholder="Ecu type" className="bg-white" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Order Id</Label>
            <Input placeholder="Order Id" className="bg-white" />
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Choose status</Label>
            <Select>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="working">Working</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Date from</Label>
            <Input type="date" className="bg-white" />
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Date to</Label>
            <Input type="date" className="bg-white" />
          </div>
        </div>
        
        <div className="flex gap-3">
          <button className="px-8 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all font-medium shadow-md">
            Filter
          </button>
          <button className="px-6 py-2.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium">
            Clear
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Order Id</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Service</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Manufacturer</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Model</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Engine</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Ecu type</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">VIN</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Description</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Priority</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Amount</th>
                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider">Options</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-cyan-50 transition-colors">
                  <td className="px-4 py-3">
                    <button 
                      onClick={() => setSelectedOrder(order)}
                      className="text-sm font-bold text-cyan-600 hover:text-cyan-700 hover:underline"
                    >
                      #{order.id}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{order.date}</td>
                  <td className="px-4 py-3 text-xs text-slate-700 font-medium">{order.service}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{order.manufacturer}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{order.model}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{order.engine}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{order.ecuType}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">{order.vin}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">{order.description || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                      order.priority === 'MS' ? 'bg-amber-100 text-amber-700' : 
                      order.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {order.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-slate-800">{order.amount}</td>
                  <td className="px-4 py-3">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                      order.status === 'Completed' ? 'bg-green-100 text-green-700' : 
                      order.status === 'Working' ? 'bg-cyan-100 text-cyan-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => setSelectedOrder(order)}
                        className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md" 
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md" title="Download">
                        <Download className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => setSelectedOrder(order)}
                        className="p-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-md" 
                        title="Open Ticket"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// File History Page Component
const FileHistoryPage = () => {
  const files = [
    {
      id: '230525-5',
      info: 'Original file MASERATI MARELLI M96XT GRANT-28040...GC301 4276.050F',
      created: '12.03.2024 17:44',
      service: 'Completed',
      dynograph: true
    },
    {
      id: '230525-4',
      info: 'Stage 1+ Hyundai County 3.9TDI',
      created: '18.05.2024 14:22',
      service: 'Completed',
      dynograph: false
    },
    {
      id: '230525-3',
      info: 'EGR+DPF Volvo FH 12.8L',
      created: '15.05.2024 09:15',
      service: 'Completed',
      dynograph: true
    }
  ];
  
  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex gap-3">
          <Input placeholder="Order Id" className="bg-white" />
          <button className="px-8 py-2.5 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors font-medium whitespace-nowrap">
            Filter
          </button>
        </div>
      </div>

      {/* Files Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Order Id</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Information about type, model and etc.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Dynograph</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-600">Options</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-600">Additional information for order</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {files.map((file) => (
                <tr key={file.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-slate-800">{file.id}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{file.info}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{file.created}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
                      {file.service}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {file.dynograph ? (
                      <button className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors flex items-center gap-1">
                        <BarChart3 className="w-3 h-3" />
                        View
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button className="px-3 py-1.5 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200 transition-colors flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        Original File
                      </button>
                      <button className="px-3 py-1.5 bg-red-100 text-red-600 text-xs rounded hover:bg-red-200 transition-colors flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        Modified
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button className="text-slate-400 hover:text-slate-600 transition-colors">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// Recharge Account Page Component
const RechargePage = () => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  const paymentMethods = [
    {
      id: 'paypal',
      name: 'PayPal',
      icon: '💳',
      description: 'Fast & Secure',
      color: 'blue'
    },
    {
      id: 'card',
      name: 'Credit/Debit Card',
      icon: '💳',
      description: 'Visa, Mastercard',
      color: 'purple'
    },
    {
      id: 'bank',
      name: 'Bank Transfer',
      icon: '🏦',
      description: 'Direct transfer',
      color: 'green'
    },
    {
      id: 'stripe',
      name: 'Stripe',
      icon: '⚡',
      description: 'Quick payment',
      color: 'indigo'
    }
  ];
  
  const packages = [
    {
      amount: 500,
      bonus: 40,
      popular: false,
      color: 'cyan'
    },
    {
      amount: 1000,
      bonus: 100,
      popular: true,
      color: 'blue'
    },
    {
      amount: 2000,
      bonus: 300,
      popular: false,
      color: 'purple'
    }
  ];
  
  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg.amount);
    setAmount(pkg.amount.toString());
  };
  
  const handleRecharge = () => {
    if (!selectedMethod) {
      alert('Please select a payment method');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    alert(`Processing payment of €${amount} via ${selectedMethod}`);
  };
  
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
            <CreditCard className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Recharge Your Account</h2>
            <p className="text-sm sm:text-base opacity-90">Top up your balance using one of our secure payment methods and receive an automatic discount on your next order!</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Column: Payment Method & Amount */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Payment Methods */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
            <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4">Choose Payment Method</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all text-left ${
                    selectedMethod === method.id
                      ? `border-${method.color}-500 bg-${method.color}-50`
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  {selectedMethod === method.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`text-3xl sm:text-4xl`}>{method.icon}</div>
                    <div className="flex-1">
                      <div className="text-sm sm:text-base font-bold text-slate-800">{method.name}</div>
                      <div className="text-xs text-slate-500">{method.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Amount Input - Hide for bank transfer */}
          {selectedMethod !== 'bank' && (
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
              <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4">Enter Amount</h3>
              
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl sm:text-3xl font-bold text-slate-400">€</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setSelectedPackage(null);
                  }}
                  placeholder="0.00"
                  className="w-full pl-12 sm:pl-14 pr-4 py-4 sm:py-5 text-2xl sm:text-3xl font-bold border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  min="10"
                  step="10"
                />
              </div>
              
              <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-slate-500">
                Minimum amount: €10 • No maximum limit
              </div>
            </div>
          )}

          {/* Bonus Packages - Hide for bank transfer */}
          {selectedMethod !== 'bank' && (
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-slate-800">Bonus Packages</h3>
                <span className="text-xs sm:text-sm text-green-600 font-medium">🎁 Get Extra Credits!</span>
              </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {packages.map((pkg, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePackageSelect(pkg)}
                  className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all ${
                    selectedPackage === pkg.amount
                      ? `border-${pkg.color}-500 bg-${pkg.color}-50`
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-2 sm:-top-3 left-1/2 -translate-x-1/2 px-2 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] sm:text-xs font-bold rounded-full shadow-lg">
                      POPULAR
                    </div>
                  )}
                  
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-black text-slate-800 mb-1 sm:mb-2">€{pkg.amount}</div>
                    <div className="inline-block px-2 sm:px-3 py-1 bg-green-100 text-green-700 text-xs sm:text-sm font-bold rounded-full">
                      +€{pkg.bonus} Bonus
                    </div>
                    <div className="mt-2 sm:mt-3 text-xs text-slate-500">
                      Total: €{pkg.amount + pkg.bonus}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="mt-3 sm:mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="text-xs sm:text-sm text-amber-800 font-medium">
                💡 Top up your balance using the pre-set amounts to receive an automatic bonus and discount on your next order!
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Right Column: Summary or Bank Instructions */}
        <div className="space-y-4 sm:space-y-6">
          {selectedMethod === 'bank' ? (
            /* Bank Transfer Instructions */
            <div className="bg-white rounded-xl p-4 sm:p-6 border-2 border-amber-400 shadow-lg sticky top-20">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b-2 border-amber-200">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🏦</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-800">Bank Transfer</h3>
                  <p className="text-xs text-amber-600 font-medium">(Invoice Required)</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <p className="text-sm text-slate-700 leading-relaxed">
                  Bank transfer payments are available upon request and require an official invoice.
                </p>
                
                <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                  <p className="text-sm font-medium text-slate-800 mb-3">To proceed, please contact our team:</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-xs text-slate-500">Email</div>
                        <a href="mailto:info@auto-remap.com" className="text-sm font-semibold text-cyan-600 hover:underline">
                          info@auto-remap.com
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-xs text-slate-500">Phone</div>
                        <a href="tel:+34692033056" className="text-sm font-semibold text-green-600 hover:underline">
                          +34 692 033 056
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-slate-600 leading-relaxed">
                  We will issue a company invoice with complete payment details tailored to your order.
                </p>
                
                <div className="bg-amber-50 rounded-lg p-3 border-2 border-amber-300">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-amber-800 mb-1">Important Notice</p>
                      <p className="text-xs text-amber-700">
                        Minimum amount for bank transfer: <span className="font-bold">€2,000</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Regular Payment Summary */
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 sm:p-6 text-white shadow-lg sticky top-20">
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Payment Summary</h3>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                  <span className="text-sm text-slate-400">Payment Method</span>
                  <span className="text-sm font-medium">
                    {selectedMethod ? paymentMethods.find(m => m.id === selectedMethod)?.name : 'Not selected'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                  <span className="text-sm text-slate-400">Amount</span>
                  <span className="text-xl font-bold text-cyan-400">
                    {amount ? `€${parseFloat(amount).toFixed(2)}` : '€0.00'}
                  </span>
                </div>
                
                {selectedPackage && (
                  <>
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <span className="text-sm text-green-400 font-medium">Bonus</span>
                      <span className="text-lg font-bold text-green-400">
                        +€{packages.find(p => p.amount === selectedPackage)?.bonus || 0}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 bg-green-500/10 rounded-lg px-3">
                      <span className="text-sm font-medium text-green-400">Total You Get</span>
                      <span className="text-2xl font-black text-green-400">
                        €{(parseFloat(amount || 0) + (packages.find(p => p.amount === selectedPackage)?.bonus || 0)).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
              
              <button
                onClick={handleRecharge}
                disabled={!selectedMethod || !amount || parseFloat(amount) <= 0}
                className={`w-full mt-4 sm:mt-6 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all ${
                  selectedMethod && amount && parseFloat(amount) > 0
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-lg'
                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                }`}
              >
                {selectedMethod && amount ? `Pay €${parseFloat(amount).toFixed(2)}` : 'Select Method & Amount'}
              </button>
              
              <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>Secure Payment • SSL Encrypted</span>
              </div>
            </div>
          )}

          {/* Quick Info */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="text-sm font-bold text-blue-800 mb-2">💼 Why Recharge?</h4>
            <ul className="text-xs text-blue-700 space-y-1.5">
              <li>✓ Faster order processing</li>
              <li>✓ No payment delays</li>
              <li>✓ Automatic bonuses</li>
              <li>✓ 24/7 available balance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tickets Page Component
const TicketsPage = () => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  
  const tickets = [
    {
      id: '230525-5',
      brand: 'MASERATI MARELLI',
      model: 'M96XT GRANT-28040 GC301',
      engine: '4276.050F',
      ecuType: '-',
      date: '30.10.2024 14:27',
      lastUpdate: '30.10.2024 14:27',
      messages: 1,
      status: 'Open'
    },
    {
      id: '230525-4',
      brand: 'Hyundai',
      model: 'County',
      engine: '3.9TDI D4DD',
      ecuType: 'Delphi DCM3.5',
      date: '07.04.2024 19:14',
      lastUpdate: '07.04.2024 19:14',
      messages: 1,
      status: 'Open'
    }
  ];
  
  const ticketMessages = [
    {
      id: 1,
      sender: 'Michael',
      type: 'customer',
      date: '30.10.2024 14:27',
      message: 'Hi, can I get status of my order file? we are waiting... Thanks!'
    },
    {
      id: 2,
      sender: 'Admin',
      type: 'admin',
      date: '30.10.2024 15:12',
      message: "Hi! We've started 10 Your test driver feedback regarding the GPS map hasn't been finalized. Hold on!"
    },
    {
      id: 3,
      sender: 'Michael',
      type: 'customer',
      date: '30.10.2024 16:45',
      message: 'DTC error? Today about how do you should check on me? Ok I back out of my work'
    },
    {
      id: 4,
      sender: 'Admin',
      type: 'admin',
      date: '30.10.2024 17:20',
      message: 'Yes! of Guarantee now Updated. If you start off the more order for you will...'
    },
    {
      id: 5,
      sender: 'Michael',
      type: 'customer',
      date: '30.10.2024 18:05',
      message: 'Did you open diff this file on me so?'
    }
  ];
  
  if (selectedTicket) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button 
          onClick={() => setSelectedTicket(null)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Tickets</span>
        </button>

        {/* Ticket Header */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Detail for order #{selectedTicket.id}</h2>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span>VAT: € 2.00</span>
                <span className="text-cyan-600 font-medium">
                  ○ Date created 30.09.2024 02:06 GMT+3
                  <br />
                  ● Date paid 01.05.2024 02:08 GMT+3
                </span>
              </div>
            </div>
            <button className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm">
              Exit
            </button>
          </div>

          {/* Ticket Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Type:</div>
              <div className="text-sm font-medium text-slate-800">Car</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Manufacturer:</div>
              <div className="text-sm font-medium text-slate-800">Land-Rover</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Model:</div>
              <div className="text-sm font-medium text-slate-800">Range Rover</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Engine:</div>
              <div className="text-sm font-medium text-slate-800">P535 • 4.4 V8 Turbo</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ ECU type:</div>
              <div className="text-sm font-medium text-slate-800">Bosch MEDC17</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Ordered options:</div>
              <div className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded inline-block">Stage 1 tuning file</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Gearbox type:</div>
              <div className="text-sm font-medium text-slate-800">Automatic transmission</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Reading method:</div>
              <div className="text-sm font-medium text-slate-800">Flashtuned Boot</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Priority answer:</div>
              <div className="text-sm font-medium text-slate-800">€30 Up</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Year of production:</div>
              <div className="text-sm font-medium text-slate-800">2023</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">→ Displacement numeration:</div>
              <div className="text-sm font-medium text-slate-800">-</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs text-slate-500 mb-1">VIN</div>
              <div className="text-sm font-medium text-slate-800">-</div>
            </div>
          </div>

          {/* Status */}
          <div className="mt-4 flex items-center gap-6">
            <div className="text-sm text-slate-600">
              <span className="font-medium">ID Ticket:</span> Open
            </div>
            <div className="text-sm text-slate-600">
              <span className="font-medium">Ticket:</span> Open
            </div>
            <div className="flex gap-2">
              <button className="p-1.5 bg-slate-200 rounded hover:bg-slate-300 transition-colors">
                <span className="text-xs text-slate-600">●</span>
              </button>
              <button className="p-1.5 bg-slate-200 rounded hover:bg-slate-300 transition-colors">
                <span className="text-xs text-slate-600">●</span>
              </button>
              <button className="p-1.5 bg-slate-200 rounded hover:bg-slate-300 transition-colors">
                <span className="text-xs text-slate-600">●</span>
              </button>
              <button className="p-1.5 bg-red-500 rounded hover:bg-red-600 transition-colors">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-600" />
              <h3 className="text-lg font-bold text-slate-800">Messages in ticket</h3>
            </div>
          </div>
          
          <div className="p-6 bg-slate-50 max-h-96 overflow-y-auto">
            <div className="space-y-4">
              {ticketMessages.map((msg) => (
                <div key={msg.id} className={`flex gap-3 ${msg.type === 'customer' ? 'justify-start' : 'justify-end'}`}>
                  {msg.type === 'customer' && (
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-orange-600" />
                    </div>
                  )}
                  
                  <div className={`max-w-md ${msg.type === 'customer' ? '' : 'order-1'}`}>
                    <div className={`px-4 py-3 rounded-lg ${
                      msg.type === 'customer' 
                        ? 'bg-white border border-slate-200' 
                        : 'bg-blue-500 text-white'
                    }`}>
                      <div className={`text-xs ${msg.type === 'customer' ? 'text-slate-500' : 'text-blue-100'} mb-1`}>
                        {msg.sender} • {msg.date}
                      </div>
                      <div className="text-sm">{msg.message}</div>
                    </div>
                  </div>
                  
                  {msg.type === 'admin' && (
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="p-6 border-t border-slate-200 bg-white">
            <div className="flex gap-3">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Message"
                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                rows={3}
              />
              <div className="flex flex-col gap-2">
                <button className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium">
                  Add file
                </button>
                <button className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-colors text-sm font-medium flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <Input placeholder="Brand" className="bg-white" />
          <Input placeholder="Model" className="bg-white" />
          <Input placeholder="Engine" className="bg-white" />
          <Input placeholder="Ecu type" className="bg-white" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Order Id</Label>
            <Input placeholder="Order Id" className="bg-white" />
          </div>
          
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Choose status</Label>
            <Select>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <button className="w-full px-6 py-2.5 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors font-medium">
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Order Id</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Engine</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Ecu type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Date create</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Last update</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Messages</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Order status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {tickets.map((ticket) => (
                <tr 
                  key={ticket.id} 
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-slate-800">{ticket.id}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{ticket.brand}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{ticket.model}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{ticket.engine}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{ticket.ecuType}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{ticket.date}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{ticket.lastUpdate}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded">
                      {ticket.messages}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                      {ticket.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Design-system preview gate — `?preview=design` mounts the
  // Phase-1 showcase page so the user can review the new primitives
  // without logging in or navigating through the app shell.
  // Defined as a state value so React hook ordering stays stable across
  // renders (we cannot early-return BEFORE hooks). The conditional is
  // safe to apply at the end since all hooks run unconditionally above.

  const [activePage, setActivePage] = useState("neworder"); // Navigation state
  const [activeTab, setActiveTab] = useState("vehicle");
  const [serverTime, setServerTime] = useState("08-04-2025 Wed 21:37:45");
  const [vehicleType, setVehicleType] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [engine, setEngine] = useState("");
  const [ecuType, setEcuType] = useState("");
  const [gearbox, setGearbox] = useState("");
  const [enginePower, setEnginePower] = useState({ hp: "", kw: "" });
  const [modelYear, setModelYear] = useState("");
  const [vin, setVin] = useState("");
  
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [fileType, setFileType] = useState("");
  const [readingMethod, setReadingMethod] = useState("");
  // Gearbox types — pulled from the admin-editable config endpoint so the
  // wizard reflects whatever the admin set in Settings → Gearbox types.
  const [dynamicGearboxTypes, setDynamicGearboxTypes] = useState(gearboxTypes);
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/gearbox-types`);
        if (!r.ok || stop) return;
        const data = await r.json();
        if (Array.isArray(data) && data.length) setDynamicGearboxTypes(data);
      } catch (_) { /* silent */ }
    })();
    return () => { stop = true; };
  }, []);
  const [priority, setPriority] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptRefund, setAcceptRefund] = useState(false);
  // Slug of the legal doc currently shown in the popup ('terms' | 'refund' | null).
  const [legalSlug, setLegalSlug] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  // Auto-open sidebar on desktop. Uses the functional setter so we
  // don't need `sidebarOpen` in the dependency array — the resize
  // listener is attached once and fires on every viewport change.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen((prev) => (prev ? prev : true));
      }
    };

    // Check on mount
    handleResize();

    // Listen for resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Update server time
  useState(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const formatted = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
        ' ' + ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][now.getDay()] + ' ' +
        now.toLocaleTimeString('en-GB');
      setServerTime(formatted);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const missingFields = useMemo(() => {
    const missing = [];
    if (!vehicleType) missing.push("Vehicle Type");
    if (!manufacturer) missing.push("Manufacturer");
    if (!model) missing.push("Model");
    if (!engine) missing.push("Engine");
    if (!ecuType) missing.push("ECU Type");
    if (!gearbox) missing.push("Gearbox Type");
    if (!modelYear) missing.push("Model Year");
    if (!enginePower.hp && !enginePower.kw) missing.push("Engine Power");
    return missing;
  }, [vehicleType, manufacturer, model, engine, ecuType, gearbox, modelYear, enginePower]);

  const isVehicleValid = missingFields.length === 0;

  const availableManufacturers = vehicleType ? vehicleData.manufacturers[vehicleType] || [] : [];
  const availableModels = manufacturer ? vehicleData.models[manufacturer] || [] : [];
  const availableEngines = model ? vehicleData.engines[model] || [] : [];
  const availableEcus = engine ? vehicleData.ecuTypes[engine] || [] : [];

  const selectedOptionsInfo = useMemo(() => {
    return selectedOptions.map(id => tuningOptions.find(o => o.id === id)).filter(Boolean);
  }, [selectedOptions]);

  // Services validation
  const servicesMissing = useMemo(() => {
    const missing = [];
    if (selectedOptions.length === 0) missing.push("At least one service");
    if (!uploadedFile) missing.push("Upload file");
    if (!readingMethod) missing.push("Reading method");
    if (!fileType) missing.push("File type");
    if (!priority) missing.push("Priority");
    return missing;
  }, [selectedOptions, uploadedFile, readingMethod, fileType, priority]);

  const isServicesValid = servicesMissing.length === 0 && acceptTerms && acceptRefund;

  // Calculate prices with discounts
  const pricingInfo = useMemo(() => {
    if (selectedOptionsInfo.length === 0) return { items: [], subtotal: 0, savings: 0 };
    const sorted = [...selectedOptionsInfo].sort((a, b) => b.price - a.price);
    let subtotal = 0;
    let originalTotal = 0;
    const items = sorted.map((opt, index) => {
      const originalPrice = opt.price;
      originalTotal += originalPrice;
      let discount = 0;
      let finalPrice = originalPrice;
      if (index === 0) {
        discount = 0;
        finalPrice = originalPrice;
      } else if (index === 1) {
        discount = 50;
        finalPrice = Math.round(originalPrice * 0.5);
      } else {
        discount = 60;
        finalPrice = Math.round(originalPrice * 0.4);
      }
      subtotal += finalPrice;
      return { ...opt, originalPrice, finalPrice, discount };
    });
    return { items, subtotal, originalTotal, savings: originalTotal - subtotal };
  }, [selectedOptionsInfo]);

  const totalPrice = useMemo(() => {
    let price = pricingInfo.subtotal;
    const priorityOption = priorities.find(p => p.id === priority);
    if (priorityOption?.extra === "+20%") price *= 1.2;
    return Math.round(price);
  }, [pricingInfo.subtotal, priority]);

  const toggleOption = (optionId) => {
    setSelectedOptions(prev => prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]);
  };

  const handlePowerChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    if (field === 'hp') {
      setEnginePower({ hp: value, kw: numValue ? Math.round(numValue * 0.7457).toString() : "" });
    } else {
      setEnginePower({ hp: numValue ? Math.round(numValue / 0.7457).toString() : "", kw: value });
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer?.files?.[0] || e.target.files?.[0];
    if (file) setUploadedFile(file);
  };

  const handleTabChange = (tab) => {
    if (tab === "services" && !isVehicleValid) return;
    setActiveTab(tab);
  };

  // Design-system preview gate — `?preview=design` short-circuits the
  // app shell so we can review Phase-1 primitives. Placed AFTER all
  // hooks so the hook call order stays stable across renders.
  if (typeof window !== 'undefined' && window.location.search.includes('preview=design')) {
    // eslint-disable-next-line global-require
    const DesignPreview = require('./components/DesignPreview').default;
    return <DesignPreview />;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Global step-up MFA modal — listens for `step-up:required` events. */}
      <StepUpModal />
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} activePage={activePage} setActivePage={setActivePage} />
      
      {/* Header */}
      <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} serverTime={serverTime} setActivePage={setActivePage} />

      {/* Main Content */}
      <main className={`pt-16 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} ml-0`}>
        <div className="p-3 sm:p-4 md:p-6">
          {/* Page Header */}
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 mb-2">
              <span>Home</span>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-slate-800">
                {activePage === 'dashboard' && 'Dashboard'}
                {activePage === 'neworder' && 'Order new file'}
                {activePage === 'orifiles' && 'ORI Files Database'}
                {activePage === 'orders' && 'Orders Info'}
                {activePage === 'history' && 'File History'}
                {activePage === 'tickets' && 'Tickets'}
                {activePage === 'recharge' && 'Recharge Account'}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
              {activePage === 'dashboard' && 'Dashboard'}
              {activePage === 'neworder' && 'Order new file'}
              {activePage === 'orifiles' && 'Original Files Database'}
              {activePage === 'orders' && 'Orders'}
              {activePage === 'history' && 'File History'}
              {activePage === 'tickets' && 'Tickets'}
              {activePage === 'recharge' && 'Recharge Account'}
            </h1>
          </div>

          {/* Conditional Page Rendering */}
          {activePage === 'dashboard' && <DashboardPage setActivePage={setActivePage} />}
          {activePage === 'orifiles' && <ORIFilesPage />}
          {activePage === 'orders' && <OrdersInfoPage />}
          {activePage === 'history' && <FileHistoryPage />}
          {activePage === 'tickets' && <TicketsPage />}
          {activePage === 'recharge' && <RechargePage />}
          
          {activePage === 'neworder' && (
            <>
              {/* Service Status Banner */}
              <div className="bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-xl p-4 mb-6 text-white">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold mb-1">File service is open now</div>
                    <div className="text-sm opacity-90">Today working time: 06:00 - 23:00 GMT+3</div>
                    <div className="text-sm opacity-90">Current time: {serverTime} GMT+3</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm opacity-90">Remaining time to close</div>
                    <div className="text-2xl font-bold">16:05:41</div>
                  </div>
                </div>
              </div>

              {/* Final Amount Banner */}
              {totalPrice > 0 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-6 text-center">
                  <span className="text-amber-700 text-lg">Final amount for payment: </span>
                  <span className="text-amber-700 text-2xl font-bold">{totalPrice} €</span>
                </div>
              )}

          {/* Order Wizard Content */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-6 bg-slate-200 p-1 rounded-lg">
              <TabsTrigger value="vehicle" className="data-[state=active]:bg-white data-[state=active]:shadow rounded-md flex items-center gap-2" data-testid="tab-vehicle">
                <Car className="w-4 h-4" />Vehicle
              </TabsTrigger>
              <TabsTrigger value="services" className={`rounded-md flex items-center gap-2 ${isVehicleValid ? 'data-[state=active]:bg-white data-[state=active]:shadow' : 'opacity-50 cursor-not-allowed'}`} data-testid="tab-services">
                <Settings className="w-4 h-4" />Options & Prices
              </TabsTrigger>
            </TabsList>

            {/* Vehicle Tab */}
            <TabsContent value="vehicle" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-5 flex items-center gap-2">
                    <Car className="w-4 h-4 text-slate-500" />Vehicle Identification
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Vehicle Type <span className="text-red-500">*</span></Label>
                      <Select value={vehicleType} onValueChange={(v) => { setVehicleType(v); setManufacturer(""); setModel(""); setEngine(""); setEcuType(""); }}>
                        <SelectTrigger data-testid="select-vehicle-type" className={`w-full bg-white h-11 rounded-lg ${!vehicleType ? 'border-red-200' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Select type..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">
                          {vehicleData.types.map(type => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Manufacturer <span className="text-red-500">*</span></Label>
                      <Select value={manufacturer} onValueChange={(v) => { setManufacturer(v); setModel(""); setEngine(""); setEcuType(""); }} disabled={!vehicleType}>
                        <SelectTrigger data-testid="select-manufacturer" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!manufacturer && vehicleType ? 'border-red-200' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Select manufacturer..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">{availableManufacturers.map(m => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Model <span className="text-red-500">*</span></Label>
                      <Select value={model} onValueChange={(v) => { setModel(v); setEngine(""); setEcuType(""); }} disabled={!manufacturer}>
                        <SelectTrigger data-testid="select-model" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!model && manufacturer ? 'border-red-200' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Select model..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg max-h-60">{availableModels.map(m => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Engine <span className="text-red-500">*</span></Label>
                      <Select value={engine} onValueChange={(v) => { setEngine(v); setEcuType(""); }} disabled={!model}>
                        <SelectTrigger data-testid="select-engine" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!engine && model ? 'border-red-200' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Select engine..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">{availableEngines.map(e => (<SelectItem key={e} value={e}>{e}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">ECU Type <span className="text-red-500">*</span></Label>
                      <Select value={ecuType} onValueChange={setEcuType} disabled={!engine}>
                        <SelectTrigger data-testid="select-ecu" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!ecuType && engine ? 'border-red-200' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Select ECU..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">{availableEcus.map(e => (<SelectItem key={e} value={e}>{e}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-5 flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-slate-500" />Technical Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Gearbox Type <span className="text-red-500">*</span></Label>
                      <Select value={gearbox} onValueChange={setGearbox}>
                        <SelectTrigger data-testid="select-gearbox" className={`w-full bg-white h-11 rounded-lg ${!gearbox ? 'border-red-200' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Select gearbox..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">{dynamicGearboxTypes.map(g => (<SelectItem key={g} value={g}>{g}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Engine Power <span className="text-red-500">*</span></Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="relative">
                          <Input data-testid="input-hp" type="number" placeholder="enter power..." value={enginePower.hp} onChange={(e) => handlePowerChange('hp', e.target.value)} className={`bg-white h-11 rounded-lg pr-10 ${!enginePower.hp ? 'border-red-200' : 'border-slate-200'}`} />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">HP</span>
                        </div>
                        <div className="relative">
                          <Input data-testid="input-kw" type="number" placeholder="enter power..." value={enginePower.kw} onChange={(e) => handlePowerChange('kw', e.target.value)} className="bg-white border-slate-200 h-11 rounded-lg pr-10" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">kW</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Model Year <span className="text-red-500">*</span></Label>
                      <Select value={modelYear} onValueChange={setModelYear}>
                        <SelectTrigger data-testid="select-year" className={`w-full bg-white h-11 rounded-lg ${!modelYear ? 'border-red-200' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Select year..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg max-h-60">{modelYears.map(y => (<SelectItem key={y} value={y}>{y}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">VIN (optional)</Label>
                      <Input data-testid="input-vin" type="text" placeholder="Enter VIN number..." value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} className="bg-white border-slate-200 h-11 rounded-lg font-mono text-sm" maxLength={17} />
                    </div>
                  </div>
                  {missingFields.length > 0 && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-700">Please fill in required fields:</p>
                          <p className="text-sm text-red-600 mt-1">{missingFields.join(", ")}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <button data-testid="btn-to-services" onClick={() => setActiveTab("services")} disabled={!isVehicleValid} className="w-full flex items-center justify-center gap-2 bg-cyan-500 text-white font-semibold px-6 py-3 rounded-lg hover:bg-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                      Go to Options and prices<ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left: Services + File */}
                <div className="lg:col-span-5 space-y-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <h3 className="text-base font-semibold text-slate-800 mb-4">Select Services</h3>
                    <div className="space-y-0 divide-y divide-slate-100">
                      {tuningOptions.map((option) => {
                        const isSelected = selectedOptions.includes(option.id);
                        return (
                          <div key={option.id} className="flex items-center gap-3 py-3">
                            <OptionIcon icon={option.icon} selected={isSelected} />
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium text-sm ${isSelected ? 'text-cyan-600' : 'text-slate-700'}`}>{option.name}</div>
                            </div>
                            <span className={`text-base font-semibold mr-2 ${isSelected ? 'text-cyan-600' : 'text-slate-800'}`}>{option.price}€</span>
                            <Switch data-testid={`option-${option.id}`} checked={isSelected} onCheckedChange={() => toggleOption(option.id)} className="data-[state=checked]:bg-cyan-500" />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* File Upload */}
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-500" />File Settings
                    </h3>
                    <div
                      className={`upload-zone p-4 text-center cursor-pointer rounded-lg mb-3 ${isDragOver ? 'dragover' : ''} ${!uploadedFile && selectedOptions.length > 0 ? 'border-amber-300' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={handleFileDrop}
                      onClick={() => document.getElementById('file-input').click()}
                      data-testid="upload-zone"
                    >
                      <input id="file-input" type="file" className="hidden" onChange={handleFileDrop} />
                      {uploadedFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span className="text-emerald-600 text-sm font-medium truncate max-w-[200px]">{uploadedFile.name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
                          <Upload className="w-4 h-4 text-amber-500" />
                          <span>Upload file</span>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={readingMethod} onValueChange={setReadingMethod}>
                        <SelectTrigger data-testid="select-reading-method" className={`w-full bg-white h-9 rounded-lg text-xs ${!readingMethod && selectedOptions.length > 0 ? 'border-amber-300' : 'border-slate-200'}`}>
                          <SelectValue placeholder="Reading method" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">{readingMethods.map(r => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
                      </Select>
                      <Select value={fileType} onValueChange={setFileType}>
                        <SelectTrigger data-testid="select-file-type" className={`w-full bg-white h-9 rounded-lg text-xs ${!fileType && selectedOptions.length > 0 ? 'border-amber-300' : 'border-slate-200'}`}>
                          <SelectValue placeholder="File type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">{fileTypes.map(f => (<SelectItem key={f} value={f}>{f}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <button data-testid="btn-back" onClick={() => setActiveTab("vehicle")} className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors text-sm">
                    <ChevronLeft className="w-4 h-4" />Back to Vehicle
                  </button>
                </div>

                {/* Middle: Instructions */}
                <div className="lg:col-span-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm lg:sticky lg:top-20">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-blue-600 rounded-full"></div>Instructions
                    </h3>
                    {selectedOptionsInfo.length > 0 ? (
                      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                        {selectedOptionsInfo.map((option, idx) => (
                          <div key={option.id} className={idx > 0 ? "pt-3 border-t border-slate-100" : ""}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-[10px] font-bold">{option.icon}</span>
                              <span className="font-medium text-sm text-slate-800">{option.name}</span>
                            </div>
                            <p className="text-slate-600 text-xs leading-relaxed">{option.instruction}</p>
                            {option.image && (
                              <div className="mt-2 relative rounded-lg overflow-hidden border border-slate-200 cursor-pointer group w-20 h-14" onClick={() => setModalImage(option.image)}>
                                <img src={option.image} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                  <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-xs italic">Select a service to see instructions</p>
                    )}
                  </div>
                </div>

                {/* Right: Priority + Summary */}
                <div className="lg:col-span-3">
                  <div className="lg:sticky lg:top-20 space-y-4">
                    {/* Priority */}
                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-500" />Priority
                      </h3>
                      <div className="space-y-2">
                        {priorities.map((p) => (
                          <button key={p.id} data-testid={`priority-${p.id}`} onClick={() => setPriority(p.id)}
                            className={`w-full p-3 rounded-lg border-2 transition-all text-left ${priority === p.id ? `${p.bg} ${p.border}` : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className={`font-semibold text-sm ${priority === p.id ? p.color : 'text-slate-700'}`}>{p.name}</span>
                                <span className="text-slate-400 text-xs ml-1">({p.time})</span>
                              </div>
                              {p.extra && (
                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-100 text-red-600">{p.extra}</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white shadow-sm">
                      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <div className="w-1 h-4 bg-cyan-400 rounded-full"></div>Summary
                      </h3>
                      
                      {pricingInfo.items.length > 0 ? (
                        <>
                          <div className="space-y-2 mb-3">
                            {pricingInfo.items.map((item, idx) => (
                              <div key={item.id} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                                    {item.icon}
                                  </span>
                                  <span className="text-slate-400 truncate text-xs">{item.name}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {item.discount > 0 ? (
                                    <>
                                      <span className="text-slate-500 line-through text-xs">{item.originalPrice}€</span>
                                      <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-1.5 py-0.5 rounded">
                                        -{item.discount}%
                                      </span>
                                      <span className="font-semibold text-white">{item.finalPrice}€</span>
                                    </>
                                  ) : (
                                    <span className="font-semibold">{item.finalPrice}€</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {pricingInfo.savings > 0 && (
                            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 mb-3">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-green-400 font-medium">You save</span>
                                <span className="text-green-400 font-bold">{pricingInfo.savings}€</span>
                              </div>
                            </div>
                          )}

                          {priority === 'highest' && (
                            <div className="flex justify-between text-sm py-2 border-t border-slate-700">
                              <span className="text-slate-400">Priority (Highest)</span>
                              <span className="text-red-400">+20%</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-slate-500 text-sm mb-4">No services selected</p>
                      )}

                      <div className="flex justify-between items-center mb-4 pt-3 border-t border-slate-700">
                        <span className="text-slate-400 text-sm">Total</span>
                        <span className="text-4xl font-black text-cyan-400">{totalPrice}€</span>
                      </div>

                      {/* Validation messages */}
                      {servicesMissing.length > 0 && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-medium text-amber-400">Please complete:</p>
                              <ul className="text-xs text-amber-300/80 mt-1">
                                {servicesMissing.map(item => (
                                  <li key={item}>• {item}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {servicesMissing.length === 0 && (!acceptTerms || !acceptRefund) && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-400" />
                            <p className="text-xs text-amber-400">Please accept Terms and Refund policy</p>
                          </div>
                        </div>
                      )}

                      <div className="space-y-2 mb-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox id="terms" data-testid="checkbox-terms" checked={acceptTerms} onCheckedChange={setAcceptTerms} className="border-slate-500" />
                          <span className="text-xs text-slate-400">I accept{' '}
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalSlug('terms'); }}
                              className="text-cyan-400 hover:underline cursor-pointer"
                              data-testid="link-terms-of-service"
                            >Terms of service</button>
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox id="refund" data-testid="checkbox-refund" checked={acceptRefund} onCheckedChange={setAcceptRefund} className="border-slate-500" />
                          <span className="text-xs text-slate-400">I accept{' '}
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalSlug('refund'); }}
                              className="text-cyan-400 hover:underline cursor-pointer"
                              data-testid="link-refund-policy"
                            >Refund policy</button>
                          </span>
                        </label>
                      </div>

                      <button
                        data-testid="btn-order"
                        disabled={!isServicesValid}
                        className="w-full flex items-center justify-center gap-2 bg-cyan-500 text-white font-bold px-6 py-3 rounded-xl hover:bg-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Rocket className="w-5 h-5" />
                        Order Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          </>
          )}
        </div>
      </main>

      {/* Image Modal */}
      <Dialog open={!!modalImage} onOpenChange={() => setModalImage(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-none">
          <button onClick={() => setModalImage(null)} className="absolute top-2 right-2 z-10 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70">
            <X className="w-5 h-5" />
          </button>
          {modalImage && <img src={modalImage} alt="Enlarged view" className="w-full h-auto" />}
        </DialogContent>
      </Dialog>

      {/* Legal document popup — opened from "Terms of service" / "Refund
          policy" links next to the acceptance checkboxes. */}
      <LegalDocModal
        slug={legalSlug}
        open={!!legalSlug}
        onClose={() => setLegalSlug(null)}
      />
    </div>
  );
}
